// frontend/src/components/PixAvulsa.js
//
// Gerar Cobrança › Pix Santander › Atendimento (Pix único) (ETAPA 6c). Fluxo: escolher o cliente Pix → valor, descrição (até 35
// caracteres) e vencimento opcional → confirmação → cobrança criada no Santander (POST /api/pix/charges) → tela com o QR, o código
// copia e cola, o link e o WhatsApp. O servidor confere tudo de novo (permissão, endereço, cobrança igual recente) e nunca repete
// uma criação sozinho. Atenção: cobrança avulsa não pode ser cancelada pelo Souli (o cancelamento do Pix só muda o status local).

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Search, Smartphone, AlertTriangle, Check, Copy, Link2, MessageCircle, X } from 'lucide-react';
import qrcode from '../lib/qrcode';
import { createPixCharge, createPixChargeShare } from '../services/pixApi';
import { buildWhatsappMessage } from '../lib/pixWhatsapp';
import DateInputBR from './DateInputBR';
import './PixCobranca.css';

const MIN_AMOUNT = 1;
const MAX_AMOUNT = 20000;
const MAX_DESCRIPTION = 35;
const MAX_DUE_DAYS = 365;

const onlyDigits = (value) => String(value == null ? '' : value).replace(/\D/g, '');
const brl = (n) => Number(n).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }).replace(/\u00a0/g, ' ');
const dateBr = (iso) => (iso ? String(iso).slice(0, 10).split('-').reverse().join('/') : '—');
const clientName = (c) => String((c && (c.full_name || c.name)) || '');
const timeBr = (iso) => { const d = new Date(iso); return Number.isNaN(d.getTime()) ? '' : d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }); };

function formatCpf(value) {
  const d = onlyDigits(value);
  return d.length === 11 ? `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}` : String(value || '');
}

function localIso(date) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}
const todayIso = () => localIso(new Date());
const maxDueIso = () => { const d = new Date(); d.setDate(d.getDate() + MAX_DUE_DAYS); return localIso(d); };

/** "89,90", "89.90" ou "1.234,56" → número com 2 casas (de R$ 1,00 a R$ 20.000,00) ou null. */
function parseUiAmount(text) {
  let s = String(text == null ? '' : text).replace(/R\$|\s/g, '');
  if (s.includes(',')) s = s.replace(/\./g, '').replace(',', '.');
  if (!/^\d+(\.\d{1,2})?$/.test(s)) return null;
  const n = Number(s);
  return n >= MIN_AMOUNT && n <= MAX_AMOUNT ? n : null;
}

/** O cadastro do cliente tem o endereço que o Pix exige? */
const hasFullAddress = (c) => /^\d{8}$/.test(onlyDigits(c.zip_code)) && !!String(c.logradouro || '').trim() && !!String(c.numero || '').trim()
  && !!String(c.bairro || '').trim() && !!String(c.city || '').trim() && /^[A-Za-z]{2}$/.test(String(c.state || '').trim());

const makeKey = () => {
  try {
    const bytes = new Uint8Array(16);
    window.crypto.getRandomValues(bytes);
    return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  } catch (error) {
    return `k${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
  }
};

async function copyText(text) {
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (error) { /* tenta o método antigo */ }
  try {
    const area = document.createElement('textarea');
    area.value = text;
    area.setAttribute('readonly', '');
    area.style.position = 'fixed';
    area.style.opacity = '0';
    document.body.appendChild(area);
    area.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(area);
    return ok;
  } catch (error) {
    return false;
  }
}

/** Texto para o usuário a partir do erro de uma chamada. */
function describeError(error) {
  switch (error && error.code) {
    case 'PIX_ACCESS_DENIED':
      return 'Seu usuário não tem permissão para criar cobrança Pix. Peça a um administrador a permissão "Gerar Cobrança" e a modalidade Pix.';
    case 'IN_PROGRESS':
      return 'Já existe uma criação em andamento para este cliente. Aguarde um instante e confira antes de tentar de novo.';
    case 'PIX_REJECTED':
      return `O Pix recusou a criação: ${error.message}`;
    case 'PIX_NOT_CONFIGURED':
      return 'A integração com o Pix ainda não está configurada no servidor. Avise o administrador.';
    case 'PIX_LOGIN_FAILED':
    case 'PIX_AUTH_REJECTED':
      return 'O Pix recusou a integração. Avise o administrador.';
    case 'PIX_UNREACHABLE':
    case 'PIX_TIMEOUT':
      return 'O Pix não respondeu. Nada foi criado; tente de novo em instantes.';
    case 'PIX_PUBLIC_BASE_MISSING':
      return 'O endereço público do link do QR não está configurado no servidor. Avise o administrador.';
    default:
      return (error && error.message) || 'Não foi possível concluir. Tente novamente.';
  }
}

/** QR em imagem a partir do código copia e cola (gerado aqui, no navegador: o código não sai da tela). */
function QrImage({ text }) {
  const source = useMemo(() => {
    try {
      const qr = qrcode(0, 'M');
      qr.addData(text);
      qr.make();
      return qr.createDataURL(6, 8);
    } catch (error) {
      return null;
    }
  }, [text]);
  if (!source) return <p className="pixc-erro">Não consegui desenhar o QR. Use o código copia e cola.</p>;
  return <img className="pixc-qr" src={source} alt="QR Code Pix" />;
}

// ───────────────────────────── componente ─────────────────────────────

/**
 * @param {Object} props
 * @param {Function} props.apiFetch fetch autenticado do Souli (a mesma da página).
 * @param {Object} [props.initialClient] cliente já escolhido (vindo da lista ou do cadastro).
 */
export default function PixAvulsa({ apiFetch, initialClient = null }) {
  const startClient = initialClient && (!initialClient.payment_type || initialClient.payment_type === 'pix') ? initialClient : null;
  const [client, setClient] = useState(startClient);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [amountText, setAmountText] = useState('');
  const [description, setDescription] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [errors, setErrors] = useState({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [apiError, setApiError] = useState('');
  const [duplicates, setDuplicates] = useState(null);
  const [stage, setStage] = useState('form'); // form | result
  const [result, setResult] = useState(null);
  const [share, setShare] = useState(null); // { url, expiresAt }
  const [sharing, setSharing] = useState(false);
  const [shareError, setShareError] = useState('');
  const [copied, setCopied] = useState('');
  const keyRef = useRef(makeKey());
  const submittingRef = useRef(false);
  const copiedTimer = useRef(null);

  const amountNumber = parseUiAmount(amountText);
  useEffect(() => () => { if (copiedTimer.current) clearTimeout(copiedTimer.current); }, []);

  const searchClients = async () => {
    const text = query.trim();
    if (text.length < 2) { setSearchError('Digite ao menos 2 letras do nome ou o CPF.'); return; }
    setSearching(true);
    setSearchError('');
    try {
      const response = await apiFetch(`/clients?payment_type=pix&search=${encodeURIComponent(text)}&limit=10`);
      const raw = (response && (response.data || response.clients)) || (Array.isArray(response) ? response : []);
      setResults(raw.filter((c) => c && c.id && c.cpf && (!c.payment_type || c.payment_type === 'pix')));
    } catch (error) {
      setResults(null);
      setSearchError('Não foi possível buscar agora. Tente novamente.');
    } finally {
      setSearching(false);
    }
  };
  const pickClient = (c) => { setClient(c); setResults(null); setQuery(''); setErrors((previous) => ({ ...previous, client: undefined })); setApiError(''); setDuplicates(null); };

  const validate = () => {
    const found = {};
    if (!client) found.client = 'Busque e selecione um cliente.';
    else if (!hasFullAddress(client)) found.client = 'O cadastro deste cliente não tem endereço completo (CEP, logradouro, número, bairro, cidade e UF). Complete o cadastro antes de cobrar.';
    if (amountNumber === null) found.amount = 'Valor inválido (de R$ 1,00 a R$ 20.000,00).';
    if (description.trim().length > MAX_DESCRIPTION) found.description = `A descrição pode ter no máximo ${MAX_DESCRIPTION} caracteres.`;
    if (dueDate) {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(dueDate)) found.dueDate = 'Data inválida. Use o formato dd/mm/aaaa.';
      else if (dueDate < todayIso()) found.dueDate = 'O vencimento não pode ser uma data passada.';
      else if (dueDate > maxDueIso()) found.dueDate = `O vencimento pode ser no máximo daqui a ${MAX_DUE_DAYS} dias.`;
    }
    return found;
  };

  const goNext = (event) => {
    if (event) event.preventDefault();
    const found = validate();
    setErrors(found);
    setApiError('');
    if (Object.keys(found).length) return;
    setConfirmOpen(true);
  };

  const submit = async (confirmDuplicate = false) => {
    if (submittingRef.current) return; // clique duplo: só uma criação por vez
    submittingRef.current = true;
    setSaving(true);
    setApiError('');
    try {
      const data = await createPixCharge({
        clientId: client.id,
        amount: amountNumber.toFixed(2),
        ...(description.trim() && { description: description.trim() }),
        ...(dueDate && { dueDate }),
        idempotencyKey: keyRef.current,
        ...(confirmDuplicate && { confirmDuplicate: true }),
      });
      setResult(data);
      setShare(null);
      setShareError('');
      setDuplicates(null);
      setConfirmOpen(false);
      setStage('result');
    } catch (error) {
      if (error && error.code === 'DUPLICATE_CHARGE') {
        setDuplicates((error.data && error.data.existing) || []);
        setConfirmOpen(false);
      } else {
        if (error && error.fields) setErrors(error.fields);
        setApiError(describeError(error));
      }
    } finally {
      submittingRef.current = false;
      setSaving(false);
    }
  };

  const doCopy = async (what, text) => {
    const ok = await copyText(text);
    setCopied(ok ? what : `${what}-falhou`);
    if (copiedTimer.current) clearTimeout(copiedTimer.current);
    copiedTimer.current = setTimeout(() => setCopied(''), 2500);
  };

  const makeShare = async () => {
    setSharing(true);
    setShareError('');
    try {
      const answer = await createPixChargeShare(result.charge.id);
      if (!answer || !/^https?:\/\//i.test(String(answer.url))) throw new Error('O Pix não devolveu um link válido.');
      setShare({ url: answer.url, expiresAt: answer.expiresAt });
    } catch (error) {
      setShareError(describeError(error));
    } finally {
      setSharing(false);
    }
  };

  const resetAll = () => {
    keyRef.current = makeKey(); // cobrança nova = chave nova (a mesma chave devolveria a cobrança anterior)
    setClient(null); setQuery(''); setResults(null); setErrors({}); setApiError(''); setDuplicates(null);
    setAmountText(''); setDescription(''); setDueDate(''); setResult(null); setShare(null); setShareError(''); setCopied('');
    setStage('form');
  };

  const closeConfirm = () => { if (!saving) { setConfirmOpen(false); setApiError(''); } };

  // ───────────── tela: resultado ─────────────
  if (stage === 'result' && result) {
    const charge = result.charge;
    const code = result.qr && result.qr.copiaECola;
    const phone = (() => { let d = onlyDigits(client && client.phone); if ((d.length === 12 || d.length === 13) && d.startsWith('55')) d = d.slice(2); return /^\d{10,11}$/.test(d) ? d : null; })();
    const message = share ? buildWhatsappMessage({ name: clientName(client), description: charge.description, amount: charge.amount, code, link: share.url, automatic: false }) : '';
    const whatsappUrl = share && phone ? `https://wa.me/55${phone}?text=${encodeURIComponent(message)}` : null;
    return (
      <div className="pixc">
        <div className="pixc-sucesso"><Check size={20} /> <strong>Cobrança criada</strong></div>
        {result.recovered && <div className="pixc-aviso" role="status"><AlertTriangle size={16} /> A conexão caiu durante a criação, mas confirmei no Pix que a cobrança foi criada (uma só).</div>}
        <dl className="pixc-resumo">
          <div><dt>Cliente</dt><dd>{clientName(client)}</dd></div>
          <div><dt>Valor</dt><dd>{brl(charge.amount)}</dd></div>
          <div><dt>Descrição</dt><dd>{charge.description}</dd></div>
          <div><dt>Vencimento</dt><dd>{charge.dueDate ? dateBr(charge.dueDate) : 'Pagamento imediato'}</dd></div>
          {charge.txid && <div><dt>Código no Santander</dt><dd>{charge.txid}</dd></div>}
        </dl>

        <h4 className="pixc-sub">QR Code para o cliente</h4>
        {code ? (
          <div className="pixc-qrbloco">
            <QrImage text={code} />
            <div className="pixc-qrlado">
              <label className="pixc-rotulo" htmlFor="pixa-codigo">Pix Copia e Cola</label>
              <textarea id="pixa-codigo" className="pixc-codigo" readOnly value={code} rows={4} />
              <button type="button" className="pixc-btn pixc-btn-primario" onClick={() => doCopy('codigo', code)}>
                <Copy size={16} /> {copied === 'codigo' ? 'Copiado!' : 'Copiar código Pix'}
              </button>
              {copied === 'codigo-falhou' && <p className="pixc-erro" role="alert">Não consegui copiar. Selecione o código acima e copie à mão.</p>}
            </div>
          </div>
        ) : (
          <div className="pixc-aviso pixc-aviso-forte" role="alert"><AlertTriangle size={16} /> A cobrança foi criada, mas o código Pix não veio na resposta. Confira a cobrança no Pix antes de enviar qualquer coisa ao cliente.</div>
        )}

        <h4 className="pixc-sub">Link para enviar ao cliente</h4>
        {!share ? (
          <>
            <button type="button" className="pixc-btn" onClick={makeShare} disabled={!code || sharing}><Link2 size={16} /> {sharing ? 'Gerando link...' : 'Gerar link para o cliente'}</button>
            {shareError && <p className="pixc-erro" role="alert">{shareError}</p>}
          </>
        ) : (
          <div className="pixc-link-bloco">
            <input className="pixc-input" readOnly value={share.url} aria-label="Link para o cliente" onFocus={(e) => e.target.select()} />
            <div className="pixc-acoes">
              <button type="button" className="pixc-btn" onClick={() => doCopy('link', share.url)}><Copy size={16} /> {copied === 'link' ? 'Copiado!' : 'Copiar link'}</button>
              {whatsappUrl && <a className="pixc-btn pixc-btn-whats" href={whatsappUrl} target="_blank" rel="noopener noreferrer"><MessageCircle size={16} /> Enviar por WhatsApp</a>}
            </div>
            {copied === 'link-falhou' && <p className="pixc-erro" role="alert">Não consegui copiar. Selecione o link acima e copie à mão.</p>}
            <p className="pixc-nota">Link válido até {dateBr(share.expiresAt)}. Quando o cliente pagar, o link deixa de mostrar o QR.</p>
            {!phone && <p className="pixc-nota">Este cliente não tem telefone válido no cadastro: copie o link e envie por onde preferir.</p>}
          </div>
        )}

        <p className="pixc-nota pixc-nota-alerta">Esta cobrança avulsa ainda não aparece em Cobranças → Pix (que lista as recorrências) e não pode ser cancelada pelo Souli: para acompanhar o pagamento, use o Pix antigo.</p>
        <div className="pixc-rodape">
          <button type="button" className="pixc-btn" onClick={resetAll}>Nova cobrança avulsa</button>
        </div>
      </div>
    );
  }

  // ───────────── tela: formulário ─────────────
  return (
    <div className="pixc">
      <h3 className="pixc-titulo"><Smartphone size={20} /> Cobrança avulsa (Pix único)</h3>

      <form onSubmit={goNext} noValidate>
        <section className="pixc-secao">
          <label className="pixc-rotulo" htmlFor="pixa-busca">Busque o cliente Pix *</label>
          {client ? (
            <div className="pixc-cliente">
              <div>
                <strong>{clientName(client)}</strong>
                <span className="pixc-sub-linha">CPF {formatCpf(client.cpf)}</span>
              </div>
              <button type="button" className="pixc-btn" onClick={() => { setClient(null); setDuplicates(null); }}>Trocar cliente</button>
            </div>
          ) : (
            <>
              <div className="pixc-busca">
                <input id="pixa-busca" className="pixc-input" type="text" value={query} placeholder="Digite nome ou CPF" onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); searchClients(); } }} />
                <button type="button" className="pixc-btn pixc-btn-primario" onClick={searchClients} disabled={searching}><Search size={16} /> {searching ? 'Buscando...' : 'Buscar'}</button>
              </div>
              {searchError && <p className="pixc-erro" role="alert">{searchError}</p>}
              {results && results.length === 0 && <p className="pixc-nota">Nenhum cliente Pix encontrado. Cadastre-o em Clientes → Cadastro de Clientes → Pix Santander.</p>}
              {results && results.length > 0 && (
                <ul className="pixc-lista">
                  {results.map((c) => (
                    <li key={c.id}>
                      <button type="button" className="pixc-item" onClick={() => pickClient(c)}>
                        <strong>{clientName(c)}</strong>
                        <span className="pixc-sub-linha">CPF {formatCpf(c.cpf)}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
          {errors.client && <p className="pixc-erro" role="alert">{errors.client}</p>}
        </section>

        <section className="pixc-grade pixa-grade">
          <div className="pixc-campo">
            <label className="pixc-rotulo" htmlFor="pixa-valor">Valor (R$) *</label>
            <input id="pixa-valor" className="pixc-input" inputMode="decimal" value={amountText} placeholder="0,00" onChange={(e) => { setAmountText(e.target.value); setErrors((p) => ({ ...p, amount: undefined })); }} />
            {errors.amount && <p className="pixc-erro" role="alert">{errors.amount}</p>}
          </div>
          <div className="pixc-campo">
            <label className="pixc-rotulo" htmlFor="pixa-vencimento">Data de vencimento (opcional)</label>
            <DateInputBR id="pixa-vencimento" className="pixc-input" value={dueDate} min={todayIso()} max={maxDueIso()} onChange={(v) => { setDueDate(v); setErrors((p) => ({ ...p, dueDate: undefined })); }} />
            <p className="pixc-nota">{dueDate ? 'Cobrança com data de vencimento.' : 'Sem data: pagamento imediato.'}</p>
            {errors.dueDate && <p className="pixc-erro" role="alert">{errors.dueDate}</p>}
          </div>
          <div className="pixc-campo pixc-campo-largo">
            <label className="pixc-rotulo" htmlFor="pixa-descricao">Descrição (opcional)</label>
            <input id="pixa-descricao" className="pixc-input" maxLength={MAX_DESCRIPTION} value={description} placeholder="Máx. 35 caracteres" onChange={(e) => { setDescription(e.target.value); setErrors((p) => ({ ...p, description: undefined })); }} />
            <p className="pixc-nota">{description.length}/{MAX_DESCRIPTION} · sem texto, vai como "Cobrança avulsa".</p>
            {errors.description && <p className="pixc-erro" role="alert">{errors.description}</p>}
          </div>
        </section>

        {duplicates && (
          <div className="pixc-aviso pixc-aviso-forte" role="alert">
            <AlertTriangle size={18} />
            <div>
              <strong>Já existe uma cobrança avulsa igual criada há pouco e ainda não paga.</strong>
              <p>Criar outra pode gerar cobrança em duplicidade.</p>
              <ul className="pixc-existentes">
                {duplicates.map((c) => <li key={c.id}>{brl(c.amount)} · {c.description}{c.dueDate ? ` · vence ${dateBr(c.dueDate)}` : ' · pagamento imediato'}{timeBr(c.createdAt) ? ` · criada às ${timeBr(c.createdAt)}` : ''}</li>)}
              </ul>
              <div className="pixc-acoes">
                <button type="button" className="pixc-btn" onClick={() => setDuplicates(null)}>Cancelar</button>
                <button type="button" className="pixc-btn pixc-btn-perigo" onClick={() => submit(true)} disabled={saving}>{saving ? 'Criando...' : 'Criar outra mesmo assim'}</button>
              </div>
            </div>
          </div>
        )}
        {apiError && !confirmOpen && <p className="pixc-erro" role="alert">{apiError}</p>}

        <div className="pixc-rodape">
          <button type="submit" className="pixc-btn pixc-btn-primario pixc-btn-grande">Próximo →</button>
        </div>
      </form>

      {confirmOpen && (
        <div className="pixc-fundo" onClick={closeConfirm}>
          <div className="pixc-modal" role="dialog" aria-modal="true" aria-labelledby="pixa-modal-titulo" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => { if (e.key === 'Escape') closeConfirm(); }}>
            <div className="pixc-modal-topo">
              <h3 id="pixa-modal-titulo">Confirmar cobrança avulsa</h3>
              <button type="button" className="pixc-fechar" onClick={closeConfirm} aria-label="Fechar" disabled={saving}><X size={18} /></button>
            </div>
            <dl className="pixc-resumo">
              <div><dt>Cliente</dt><dd>{clientName(client)}</dd></div>
              <div><dt>CPF</dt><dd>{formatCpf(client.cpf)}</dd></div>
              <div><dt>Valor</dt><dd className="pixc-valor">{brl(amountNumber)}</dd></div>
              <div><dt>Descrição</dt><dd>{description.trim() || 'Cobrança avulsa'}</dd></div>
              <div><dt>Vencimento</dt><dd>{dueDate ? dateBr(dueDate) : 'Pagamento imediato'}</dd></div>
            </dl>
            <p className="pixc-aviso"><AlertTriangle size={16} /> Isto cria uma cobrança <strong>real</strong> no Santander. Depois de criada, <strong>não dá para cancelá-la pelo Souli</strong>.</p>
            {apiError && <p className="pixc-erro" role="alert">{apiError}</p>}
            <div className="pixc-acoes pixc-acoes-fim">
              <button type="button" className="pixc-btn" onClick={closeConfirm} disabled={saving}>Voltar</button>
              <button type="button" className="pixc-btn pixc-btn-primario" onClick={() => submit(false)} disabled={saving}>{saving ? 'Criando...' : 'Criar cobrança'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
